import * as Portal from "@radix-ui/react-portal";
import * as Toast from "@radix-ui/react-toast";
import type { ComponentPropsWithRef, ElementRef } from "react";
import { forwardRef } from "react";
import { cn } from "../../helpers/styles";

export const ToastProvider = Toast.Provider;
export const ToastPortal = Portal.Root;

export const ToastRoot = forwardRef<
	ElementRef<typeof Toast.Root>,
	ComponentPropsWithRef<typeof Toast.Root>
>(({ children, className, ...props }, ref) => {
	return (
		<Toast.Root
			ref={ref}
			className={cn(
				"fixed inset-0 bottom-0 top-auto z-[100] flex max-h-fit w-full gap-xl rounded-t-xl bg-white p-xl shadow-3 duration-300 animate-in fade-in max-sm:bottom-0 lg:bottom-auto lg:left-auto lg:right-xl lg:top-xl lg:max-w-sm lg:rounded-xl",
				className,
			)}
			{...props}
		>
			{children}
		</Toast.Root>
	);
});

ToastRoot.displayName = "ToastRoot";

export const ToastViewport = forwardRef<
	ElementRef<typeof Toast.Viewport>,
	ComponentPropsWithRef<typeof Toast.Viewport>
>(({ children, className, ...props }, ref) => {
	return (
		<Toast.Viewport
			tabIndex={0}
			ref={ref}
			className={cn("fixed top-0 left-0 z-[100]", className)}
			{...props}
		>
			{children}
		</Toast.Viewport>
	);
});

ToastViewport.displayName = "ToastViewport";

export const ToastTitle = forwardRef<
	ElementRef<typeof Toast.Title>,
	ComponentPropsWithRef<typeof Toast.Title>
>(({ children, className, ...props }, ref) => {
	return (
		<Toast.Title ref={ref} className={cn("mb-xs text-md font-semibold", className)} {...props}>
			{children}
		</Toast.Title>
	);
});

ToastTitle.displayName = "ToastTitle";

export const ToastDescription = forwardRef<
	ElementRef<typeof Toast.Description>,
	ComponentPropsWithRef<typeof Toast.Description>
>(({ children, className, ...props }, ref) => {
	return (
		<Toast.Description ref={ref} className={cn("text-sm text-gray-700", className)} {...props}>
			{children}
		</Toast.Description>
	);
});

ToastDescription.displayName = "ToastDescription";

export const ToastAction = forwardRef<
	ElementRef<typeof Toast.Action>,
	ComponentPropsWithRef<typeof Toast.Action>
>(({ children, className, ...props }, ref) => {
	return (
		<Toast.Action ref={ref} className={cn("", className)} {...props}>
			{children}
		</Toast.Action>
	);
});

ToastAction.displayName = "ToastAction";

export const ToastClose = forwardRef<
	ElementRef<typeof Toast.Close>,
	ComponentPropsWithRef<typeof Toast.Close>
>(({ children, className, ...props }, ref) => {
	return (
		<Toast.Close
			ref={ref}
			className={cn("absolute right-xl top-xl flex cursor-pointer", className)}
			{...props}
		>
			{children}
		</Toast.Close>
	);
});

ToastClose.displayName = "ToastClose";
